import * as React from 'react';

import useSupportsObjectFit from '../../hooks/useSupportsObjectFit';
import ImageViewModel from '../../view-models/ImageViewModel';
import { ImageSizes, generateSizes } from './image-sources';

export type ImageProps = ImageViewModel & {
  /** `sizes` is a responsive array of different image widths.
   * You should ensure this is filled out so it matches the component that renders it as closely as possible.
   * It can take any valid CSS size, including `calc()` methods.
   * */
  sizes: ImageSizes;
  loading?: 'lazy' | 'eager';
  className?: string;
  style?: React.CSSProperties;
};

const Image = React.forwardRef(
  (
    {
      src,
      srcset,
      sizes,
      width,
      height,
      loading,
      alt,
      className,
      style,
    }: ImageProps,
    imgRef: React.RefObject<HTMLImageElement>,
  ) => {
    const supportsObjectFit = useSupportsObjectFit();

    if (supportsObjectFit === false) {
      // If Objectfit isn't supported, we fall back to div with backgroundImage
      return (
        <div
          ref={imgRef}
          className={className}
          style={style}
          css={{
            width: '100%',
            height: 'auto',
            backgroundImage: `url(${src})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        />
      );
    }

    return (
      <img
        ref={imgRef}
        css={{
          width: '100%',
          height: 'auto',
          objectFit: 'cover',
          visibility: 'hidden',
          '@supports (object-fit: cover)': {
            visibility: 'visible',
          },
          '&:-moz-loading': {
            visibility: 'hidden',
          },
        }}
        style={style}
        src={src}
        srcSet={srcset}
        sizes={srcset && generateSizes(sizes)}
        width={width}
        height={height}
        loading={loading}
        alt={alt}
        className={className}
      />
    );
  },
);

export default Image;
